import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import api, { getSession } from "../utils/api";

interface AuthContextType {
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string, rememberMe?: boolean, appName?: string) => Promise<void>;
  logout: () => Promise<void>;
  user: { id: string; email: string; issuperadmin?: boolean; activeorganizationid?: string | null; app_name?: string; firstname?: string; lastname?: string } | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const validateSession = async (preserveUser: boolean = false) => {
    try {
      const storedUser = localStorage.getItem("user");
      let userData = storedUser ? JSON.parse(storedUser) : null;
      if (userData?.email && userData?.id && preserveUser) {
        setUser(userData);
        setIsAuthenticated(true);
      }
      // Fetch fresh user data from /auth/me
      const sessionResponse = await getSession();
      console.log('validateSession: sessionResponse from /auth/me:', sessionResponse);
      // Unwrap sessionUser from response.data
      const sessionUser = sessionResponse?.data || sessionResponse;
      console.log('validateSession: sessionUser:', sessionUser);
      if (sessionUser?.email && sessionUser?.id) {
        const updatedUser = {
          id: sessionUser.id,
          email: sessionUser.email,
          issuperadmin: sessionUser.issuperadmin !== undefined ? sessionUser.issuperadmin : false,
          activeorganizationid: sessionUser.activeorganizationid ?? null,
          app_name: sessionUser.app_name ?? userData?.app_name ?? "Optenics",
          firstname: sessionUser.firstname ?? userData?.firstname,
          lastname: sessionUser.lastname ?? userData?.lastname
        };
        console.log('validateSession: updatedUser:', updatedUser);
        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setIsAuthenticated(true);
        return true;
      } else if (preserveUser && userData?.email && userData?.id) {
        // Preserve user data during login if /auth/me fails
        const preservedUser = {
          id: userData.id,
          email: userData.email,
          issuperadmin: userData.issuperadmin !== undefined ? userData.issuperadmin : false,
          activeorganizationid: userData.activeorganizationid ?? null,
          app_name: userData.app_name ?? "Optenics",
          firstname: userData.firstname,
          lastname: userData.lastname
        };
        console.log('validateSession: preservedUser:', preservedUser);
        setUser(preservedUser);
        localStorage.setItem("user", JSON.stringify(preservedUser));
        setIsAuthenticated(true);
        return true;
      } else {
        setUser(null);
        localStorage.removeItem("user");
        setIsAuthenticated(false);
        return false;
      }
    } catch (err: any) {
      console.error('validateSession error:', err);
      if (preserveUser && userData?.email && userData?.id) {
        const preservedUser = {
          id: userData.id,
          email: userData.email,
          issuperadmin: userData.issuperadmin !== undefined ? userData.issuperadmin : false,
          activeorganizationid: userData.activeorganizationid ?? null,
          app_name: userData.app_name ?? "Optenics",
          firstname: userData.firstname,
          lastname: userData.lastname
        };
        console.log('validateSession: preservedUser:', preservedUser);
        setUser(preservedUser);
        localStorage.setItem("user", JSON.stringify(preservedUser));
        setIsAuthenticated(true);
        return true;
      }
      setUser(null);
      localStorage.removeItem("user");
      setIsAuthenticated(false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string, rememberMe: boolean = false, appName: string = "Optenics") => {
    try {
      const res = await api.post("/auth/login", {
        email,
        password,
        rememberMe,
        app_name: appName,
      });
      const userData = res.data;
      console.log('login: userData from /auth/login:', userData);
      if (!userData?.email || !userData?.id) {
        throw new Error("Invalid response: email or id not found");
      }
      setUser(userData);
      setIsAuthenticated(true);
      localStorage.setItem("user", JSON.stringify(userData));
      // Only navigate if session is valid
      const isSessionValid = await validateSession(true);
      if (isSessionValid) {
        navigate("/dashboard", { replace: true });
      } else {
        throw new Error("Session validation failed after login");
      }
    } catch (err: any) {
      setUser(null);
      localStorage.removeItem("user");
      setIsAuthenticated(false);
      throw new Error(err.response?.data?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Silent fail for logout
    }
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem("user");
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    validateSession();
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, loading, login, logout, user }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
